import React, { useState, useRef, useEffect } from 'react';
import { Flame, Heart, Music, Send, Crown, Gem, Star, Users, Coins } from 'lucide-react';
import { formatEuro } from './liveTheme';

/* ═══════════════════════════════════════════════════════════════════════════════
   LIVE DESKTOP CHAT PANEL — Twitch-Style Side Panel
   
   Fixed sidebar for Desktop/Tablet (≥768px):
   - 30% width on the right side
   - Full height chat with scrollable messages
   - Fixed header with session info
   - Fixed footer with input + reactions
   - No overlay on video
═══════════════════════════════════════════════════════════════════════════════ */

/* ── Chat Message Component ── */
const ChatMessage = ({ message }) => {
  const isTip = message.type === 'tip';
  const tierColors = {
    diamond: 'text-[#d4a853]',
    spotlight: 'text-[#d4a853]/80',
    rising: 'text-white/90',
    base: 'text-white/70',
  };
  
  const userColor = tierColors[message.user_tier] || tierColors.base;
  
  return (
    <div 
      data-testid="desktop-chat-message"
      className={`px-3 py-2 ${isTip ? 'bg-[#d4a853]/10 border-l-2 border-[#d4a853]' : 'hover:bg-white/5'} transition`}
    >
      <div className="flex items-start gap-2">
        {/* Avatar */}
        <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${isTip ? 'bg-[#d4a853]/20 text-[#d4a853]' : 'bg-white/10 text-white/60'}`}>
          {message.user_name?.charAt(0).toUpperCase() || '?'}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-[12px] font-semibold ${userColor} truncate`}>
              {message.user_name || 'Anon'}
            </span>
            {isTip && (
              <span className="flex items-center gap-1 text-[10px] text-[#d4a853] font-medium">
                <Coins size={10} />
                {formatEuro(message.amount)}
              </span>
            )}
          </div>
          <p className={`text-[13px] break-words ${isTip ? 'text-[#d4a853]/90' : 'text-white/80'}`}>
            {message.message}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Reactions Bar ── */
const ReactionsBar = ({ onReact, token, disabled, reactionCounts = {} }) => {
  const buttons = [
    { type: 'heart', Icon: Heart, color: 'hover:bg-pink-500/20 hover:text-pink-400', activeColor: 'text-pink-400' },
    { type: 'fire', Icon: Flame, color: 'hover:bg-orange-500/20 hover:text-orange-400', activeColor: 'text-orange-400' },
    { type: 'music', Icon: Music, color: 'hover:bg-[#d4a853]/20 hover:text-[#d4a853]', activeColor: 'text-[#d4a853]' },
  ];

  return (
    <div 
      data-testid="desktop-reactions-bar"
      className="flex items-center gap-1"
    >
      {buttons.map(({ type, Icon, color }) => (
        <button
          key={type}
          data-testid={`desktop-reaction-${type}`}
          onClick={() => onReact?.(type)}
          disabled={!token || disabled}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 
                     text-white/50 text-xs transition-all ${color}
                     disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <Icon size={14} />
          {reactionCounts[type] > 0 && (
            <span className="text-[10px]">{reactionCounts[type]}</span>
          )}
        </button>
      ))}
    </div>
  );
};

/* ── Quick Tips Bar ── */
const QuickTipsBar = ({ onTip, token, walletBalance }) => {
  const tips = [
    { amount: 2, Icon: Star, label: '2€' },
    { amount: 5, Icon: Gem, label: '5€' },
    { amount: 10, Icon: Crown, label: '10€' },
  ];

  return (
    <div 
      data-testid="desktop-quick-tips"
      className="flex items-center gap-1"
    >
      {tips.map(({ amount, Icon, label }) => (
        <button
          key={amount}
          data-testid={`desktop-tip-${amount}`}
          onClick={() => token && onTip?.(amount, '')}
          disabled={!token || (walletBalance !== null && walletBalance < amount)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#d4a853]/10 border border-[#d4a853]/30 
                    text-[#d4a853] text-xs font-medium transition-all
                    hover:bg-[#d4a853]/20 hover:border-[#d4a853]/50
                    disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Icon size={12} />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
};

/* ── Main Desktop Chat Panel ── */
export const LiveDesktopChatPanel = ({
  session,
  messages = [],
  onSendChat,
  onSendTip,
  onReact,
  token,
  lang = 'es',
  walletBalance = null,
  isHost = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Filter and sort messages
  const chatMessages = messages.filter(m => m.type === 'chat' || m.type === 'tip');
  
  // Calculate reaction counts
  const reactionCounts = messages
    .filter(m => m.type === 'reaction')
    .reduce((acc, m) => {
      const type = m.reaction || m.message;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages.length]);

  const handleSend = () => {
    if (inputValue.trim() && onSendChat) {
      onSendChat(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div 
      data-testid="desktop-chat-panel"
      className="h-full flex flex-col bg-[#0c0c0c] border-l border-white/10"
    >
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-white/10 bg-[#0a0a0a]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm font-semibold text-white">
              {lang === 'es' ? 'Chat en vivo' : 'Live Chat'}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/50">
            <span className="flex items-center gap-1">
              <Users size={12} />
              {session?.viewers || 0}
            </span>
            {/* Only host sees donations - private info */}
            {isHost && (session?.total_donations || 0) > 0 && (
              <span className="flex items-center gap-1 text-[#d4a853]">
                <Coins size={12} />
                {formatEuro(session?.total_donations || 0)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
      >
        {chatMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-white/30">
              {lang === 'es' ? 'Sé el primero en escribir...' : 'Be the first to chat...'}
            </p>
          </div>
        ) : (
          <div className="py-2">
            {chatMessages.map((msg, i) => (
              <ChatMessage key={msg.id || i} message={msg} />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Footer - Actions */}
      <div className="flex-shrink-0 border-t border-white/10 bg-[#0a0a0a]">
        {/* Reactions + Tips Row */}
        <div className="px-3 py-2 flex items-center justify-between border-b border-white/5">
          <ReactionsBar 
            onReact={onReact}
            token={token}
            reactionCounts={reactionCounts}
          />
          <QuickTipsBar 
            onTip={onSendTip}
            token={token}
            walletBalance={walletBalance}
          />
        </div>
        
        {/* Input Row */}
        {token ? (
          <div className="px-3 py-3">
            <div className="flex items-center gap-2">
              <input
                data-testid="desktop-chat-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={lang === 'es' ? 'Escribe un mensaje...' : 'Type a message...'}
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                          text-sm text-white placeholder-white/40 outline-none
                          focus:border-white/20 transition"
              />
              <button
                data-testid="desktop-chat-send"
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="w-9 h-9 rounded-lg bg-[#d4a853] flex items-center justify-center 
                          text-black transition hover:bg-[#e5b963]
                          disabled:opacity-40 disabled:bg-white/10 disabled:text-white/40"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div className="px-3 py-3">
            <p className="text-xs text-white/40 text-center">
              {lang === 'es' ? 'Inicia sesión para chatear' : 'Sign in to chat'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
